import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import strings from "translate/language";
import {
  visitorList,
  getVisiterList,
  visitorFormUpdate,
} from "reduxStore/action/visitorActions";
import { useDispatch, useSelector } from "react-redux";
import {
  CallCenter,
  CPAgent,
  MIS_RoleId,
  isGreRoleId,
  ClosingTL,
  CSTL,
  SiteHead,
} from "utils/constants";
import AllocateSM from "component/common/AllocateCM";
import VisitorCmAllocationCell from "component/common/VisitorCmAllocationCell";
import { getConfiguration } from "reduxStore/action/masterActions";
import TableDataNotFound from "component/TableDataNotFound";
import TableListSkeleton from "component/common/TableListSkeleton";
import { handleDetailResponse, handleListResponse, seo } from "utils/utils";
import Breadcrumb from "component/common/breadcrumb";
import usePermission from "utils/usePermission";
import moment from "moment";
import usePage from "utils/usePage";
import Pagination from "component/common/Pagination";
import { getReportPropertyList } from "reduxStore/action/reportActions";
import Select from "react-select";
import { getAgentProperty } from "reduxStore/action/propertyActions";
import {
  getVisitorCurrentCmId,
  getVisitorCurrentCmName,
  isVisitorAllocatedToCm,
} from "utils/visitorCmAllocation";
import { formatAppointmentCellValue } from "utils/appointmentListStatusDisplay";
import { getStatusBadgeIconClass } from "utils/statusBadgeIcons";
import {
  getVisitorDisplayBadgeClass,
  resolveVisitorDisplayStatus,
} from "utils/visitorStatusDisplay";
import { LATEST_LEAD_STATUS_FILTER_OPTIONS } from "utils/latestLeadStatusFilter";
import "./VisitorListing.css";

const VisitorStatusIcon = ({ badgeClass }) => (
  <span className="visitor-status-badge__icon" aria-hidden="true">
    <i className={getStatusBadgeIconClass(badgeClass)} />
  </span>
);

const VISITOR_SORT_OPTIONS = {
  NEWEST: "newest",
  OLDEST: "oldest",
  SCORE_HIGH: "score_high",
  SCORE_LOW: "score_low",
};

const mapSortToApiParams = (sortBy) => {
  if (sortBy === VISITOR_SORT_OPTIONS.OLDEST) {
    return { sort_by: "oldest" };
  }
  if (sortBy === VISITOR_SORT_OPTIONS.SCORE_HIGH) {
    return { visit_score: "2" };
  }
  if (sortBy === VISITOR_SORT_OPTIONS.SCORE_LOW) {
    return { visit_score: "1" };
  }
  return { sort_by: "newest" };
};

const getDefaultDateRange = () => {
  const today = moment().format("YYYY-MM-DD");
  return { start_date: today, end_date: today };
};

const emptyVisitorFilters = (dates = getDefaultDateRange()) => ({
  start_date: dates.start_date,
  end_date: dates.end_date,
  limit: "",
  offset: "",
  search_by_visisor_name: "",
  search_configuration: "",
  search_property_name: "",
  search_by_mobile_number: "",
  qualified: "",
  lead_priority: "",
  lead_status: "",
  latest_lead_status: "",
  site: "",
});

const renderVisitorCell = (value, toneClass = "") => {
  const text = formatAppointmentCellValue(value);
  const tone = text === "-" ? "visitor-listing__empty" : toneClass;
  return (
    <span className={`visitor-listing__cell ${tone}`.trim()}>{text}</span>
  );
};

const getVisitorSourceName = (item) => {
  if (item?.created_name === "Mathan LMS") return "Mathan LMS";
  const createdName = String(item?.created_name || "").trim();
  if (createdName) return createdName;
  const createBy = String(item?.create_by || "").trim();
  if (createBy && !/^[a-f0-9]{24}$/i.test(createBy)) return createBy;
  return String(item?.created_for_sm_name || "").trim();
};

const formatVisitorBudget = (item, isCpView) => {
  if (item?.budget_amount) {
    return item.budget_amount;
  }

  if (isCpView) {
    const min = `${item?.min_rate || ""}${item?.min_rate ? item.min_rate_type : ""}`.trim();
    const max = `${item?.max_rate || ""}${item?.max_rate ? item.max_rate_type : ""}`.trim();
    if (!min && !max) return null;
    return `${min}${min && max ? " - " : ""}${max}`.trim();
  }

  const min = `${item?.min_budget || ""}${item?.min_budget ? item.min_budget_type : ""}`.trim();
  const max = `${item?.max_budget || ""}${item?.max_budget ? item.max_budget_type : ""}`.trim();
  if (!min && !max) return null;
  return `${min}${min && max ? " - " : ""}${max}`.trim();
};

const Visitor = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [page, setPage] = usePage();
  const [formVaild, setFormVaild] = useState({});
  const [endDateDisable, setEndDateDisable] = useState(false);
  const [getvisitorList, setVisitorList] = useState([]); // Always initialize as array
  const [propertyList, setPropertyList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [sortBy, setSortBy] = useState(VISITOR_SORT_OPTIONS.NEWEST);
  const lastAllocatedLeadIdRef = useRef(null);
  const initialLoadDoneRef = useRef(false);
  const visitorListSelector = useSelector((state) => state?.visitor);
  const ReportListSelector = useSelector((state) => state.report);
  const propertySelector = useSelector((state) => state.propertyData);
  const visitorConfiguration = useSelector(
    (state) => state.visitorConfiguration,
  );

  const { response: res } = useSelector((state) => state.login);
  const misRole = MIS_RoleId === res?.data?.role_id;
  const roleId = res?.data?.role_id;
  // Convert both to strings for comparison to handle ObjectId vs string
  const currentRoleId = String(roleId || "").trim();
  const closingTlRoleId = String(ClosingTL || "").trim();
  const cstlRoleId = String(CSTL || "").trim();
  const siteHeadRoleId = String(SiteHead || "").trim();
  const isGRE = isGreRoleId(roleId);
  const canAllocateToCM =
    isGRE ||
    currentRoleId === closingTlRoleId ||
    currentRoleId === cstlRoleId ||
    currentRoleId === siteHeadRoleId;

  const cpId = useSelector((state) => {
    let response = handleDetailResponse(state.login.response?.data);
    if (response.role_id === CallCenter) {
      return true;
    } else if (response.role_id === CPAgent) {
      return response?.cp_id;
    }
    return false;
  });

  useEffect(() => {
    dispatch(visitorFormUpdate({}));
  }, [dispatch]);

  const [visitorConfigurationData, setvisitorConfigurationData] = useState([]);
  const { create, view, edit, isAdmin, bulk } = usePermission({
    create: "add_visitor",
    view: "view_visitor",
    edit: "edit_visitor",
    bulk: "bulk_upload",
  });
  const navigatpage = async (navname) => {
    navigate(navname);
  };
  const [form, setForm] = useState(() => {
    const defaults = getDefaultDateRange();
    const qStart = query.get("start_date");
    const qEnd = query.get("end_date");
    return emptyVisitorFilters({
      start_date: qStart || defaults.start_date,
      end_date: qEnd || defaults.end_date,
    });
  });

  useEffect(() => {
    dispatch(visitorFormUpdate({}));
  }, [dispatch]);

  useEffect(() => {
    if (form.start_date === "") {
      setEndDateDisable(true);
    } else {
      setEndDateDisable(false);
    }
  }, [form.start_date]);

  useEffect(() => {
    seo({
      title: "Visitor Listing",
      metaDescription: "Visitor listing",
      canonicalLink: window.location.href,
    });
  }, []);

  const handleValid = () => {
    let err = { ...formVaild };
    let formError = false;
    if (form.start_date !== "" && form.end_date === "") {
      formError = true;
      err["end_date"] = strings.endDateErr;
    }
    setFormVaild(err);
    return formError;
  };

  const buildListParams = (offset, activeSort = sortBy, source = form) => {
    const params = {
      limit: 10,
      offset,
      start_date: source.start_date,
      end_date: source.end_date,
      search_configuration: source.search_configuration,
      search_by_visisor_name: source.search_by_visisor_name,
      search_by_mobile_number: source.search_by_mobile_number,
      qualified: source.qualified,
      lead_priority: source.lead_priority,
      lead_status: source.lead_status,
      latest_lead_status: source.latest_lead_status,
      ...mapSortToApiParams(activeSort),
    };

    if (source?.site?._id) {
      params.property_id = source.site._id;
    }

    return params;
  };

  const fetchVisitorList = (offset, options = {}) => {
    setLoading(true);
    const source = options.formOverride || form;
    const commonParams = buildListParams(
      offset,
      options.sortBy || sortBy,
      source,
    );

    if (isAdmin) {
      dispatch(
        visitorList({
          ...commonParams,
          search_type: cpId ? 1 : 2,
        }),
      );
    } else {
      dispatch(getVisiterList(commonParams));
    }
  };

  const filterDetail = () => {
    if (!handleValid()) {
      setPage(0);
      fetchVisitorList(0);
    }
  };

  const filterReset = () => {
    const resetForm = emptyVisitorFilters();
    setSortBy(VISITOR_SORT_OPTIONS.NEWEST);
    setForm(resetForm);
    setFormVaild({ ...formVaild, end_date: "" });
    setPage(0);
    fetchVisitorList(0, {
      sortBy: VISITOR_SORT_OPTIONS.NEWEST,
      formOverride: resetForm,
    });
  };

  const handleSortChange = (e) => {
    const nextSort = e.target.value;
    setSortBy(nextSort);
    setPage(0);
    fetchVisitorList(0, { sortBy: nextSort });
  };

  const handleInputField = (e) => {
    const nextFormState = { ...form, [e.target.name]: e.target.value };
    setForm(nextFormState);
    if (e.target.name === "end_date") {
      setFormVaild({ ...formVaild, end_date: "" });
    }
  };

  const handlePageClick = (newOffset) => {
    setPage(newOffset);
    fetchVisitorList(newOffset);
  };

  // Initial list load (respects page from URL on first mount only)
  useEffect(() => {
    if (initialLoadDoneRef.current) {
      return;
    }
    initialLoadDoneRef.current = true;
    fetchVisitorList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Populate visitor list
  useEffect(() => {
    if (visitorListSelector?.response?.status === 200) {
      let visitorData = visitorListSelector?.response?.data || [];
      const safeVisitorData = Array.isArray(visitorData) ? visitorData : [];
      // GRE: after allocate, keep allocated lead out of list when refetch completes
      if (lastAllocatedLeadIdRef.current) {
        const idToExclude = String(lastAllocatedLeadIdRef.current);
        visitorData = safeVisitorData.filter(
          (v) => String(v._id || v.lead_id || "") !== idToExclude,
        );
        lastAllocatedLeadIdRef.current = null;
      } else {
        visitorData = safeVisitorData;
      }
      setVisitorList(visitorData);
      setLoading(false);
    } else {
      setVisitorList([]);
      setLoading(false);
    }
  }, [visitorListSelector, isGRE]);

  // Load property list
  useEffect(() => {
    if (isAdmin) {
      dispatch(getReportPropertyList({ limit: 10, offset: 0 }));
    } else {
      dispatch(getAgentProperty({ offset: 0, limit: "" }));
    }
  }, [dispatch]);

  useEffect(() => {
    if (visitorConfiguration?.response?.status === 200) {
      setvisitorConfigurationData(visitorConfiguration.response.data);
    } else {
      setvisitorConfigurationData([]);
    }
  }, [visitorConfiguration]);

  useEffect(() => {
    if (!isAdmin && propertySelector?.response?.status === 200) {
      const filtered = propertySelector.response.data?.filter(
        (item) => item.status === true,
      );
      setPropertyList(filtered?.length > 0 ? handleListResponse(filtered) : []);
    }
  }, [propertySelector, isAdmin]);

  useEffect(() => {
    if (isAdmin && ReportListSelector?.reportPropertyList?.status === 200) {
      setPropertyList(
        handleListResponse(ReportListSelector.reportPropertyList.data),
      );
    }
  }, [ReportListSelector]);

  useEffect(() => {
    dispatch(getConfiguration());
  }, [dispatch]);

  return (
    <div className="page-body justo-visitor-page">
      <Breadcrumb
        title={strings.visitor}
        parentListing={null}
        titleactive={strings.visitorList}
      />
      <div className="container-fluid justo-visitor">
        <div className="justo-visitor__card">
          <div className="justo-visitor__top">
            <div className="justo-visitor__title-block">
              <h1 className="justo-visitor__title">{strings.visitorList}</h1>
              <p className="justo-visitor__subtitle">
                {strings.visitorListSubtitle}
              </p>
            </div>
            <div className="justo-visitor__top-actions">
              {!isAdmin && create ? (
                <button
                  type="button"
                  className="justo-visitor__btn justo-visitor__btn--solid"
                  onClick={() =>
                    navigatpage(`/visitor_form?formSessionKey=${Date.now()}`)
                  }
                >
                  {strings.addNewVisitor}
                </button>
              ) : null}
            </div>
          </div>

          <form
            className="justo-visitor__filters"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="justo-visitor__filters-grid">
              <label className="justo-visitor__field">
                <span className="justo-visitor__label">{strings.startDate}</span>
                <input
                  className="form-control justo-visitor__input"
                  type="date"
                  name="start_date"
                  value={form.start_date}
                  onChange={handleInputField}
                />
              </label>
              <label className="justo-visitor__field">
                <span className="justo-visitor__label">{strings.endDate}</span>
                <input
                  className="form-control justo-visitor__input"
                  type="date"
                  name="end_date"
                  value={form.end_date}
                  onChange={handleInputField}
                  min={form.start_date}
                  disabled={endDateDisable}
                />
                {formVaild.end_date ? (
                  <span className="justo-visitor__error">
                    {formVaild.end_date}
                  </span>
                ) : null}
              </label>
              <label className="justo-visitor__field">
                <span className="justo-visitor__label">
                  {strings.searchByVisitorName}
                </span>
                <input
                  className="form-control justo-visitor__input"
                  type="text"
                  autoComplete="off"
                  placeholder={strings.searchVisitorName}
                  name="search_by_visisor_name"
                  value={form.search_by_visisor_name}
                  onChange={handleInputField}
                />
              </label>
              <label className="justo-visitor__field">
                <span className="justo-visitor__label">
                  {strings.searchByConfiguration}
                </span>
                <select
                  className="form-control justo-visitor__input"
                  name="search_configuration"
                  value={form.search_configuration}
                  onChange={handleInputField}
                >
                  <option value="">{strings.selectConfiguration}</option>
                  {visitorConfigurationData?.length ? (
                    visitorConfigurationData.map((item, index) => (
                      <option key={index} value={item._id}>
                        {item.title}
                      </option>
                    ))
                  ) : (
                    <option>{strings.Loading}</option>
                  )}
                </select>
              </label>
              <div className="justo-visitor__field">
                <span className="justo-visitor__label">
                  {strings.searchByPropertyName}
                </span>
                <Select
                  classNamePrefix="justo-visitor-select"
                  isSearchable
                  isClearable
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  onInputChange={(e) => {
                    if (isAdmin) {
                      if (e.length > 1) {
                        dispatch(
                          getReportPropertyList({
                            search_site: e,
                            isLoadMore: true,
                          }),
                        );
                      } else {
                        dispatch(
                          getReportPropertyList({
                            limit: 10,
                            offset: 0,
                            isLoadMore: true,
                          }),
                        );
                      }
                    }
                  }}
                  placeholder={strings.searchByPropertyName}
                  options={propertyList}
                  getOptionLabel={(option) => `${option.property_title}`}
                  getOptionValue={(option) => option._id}
                  name="site"
                  value={form.site}
                  onChange={(e) => {
                    setForm({ ...form, site: e });
                  }}
                />
              </div>
              <label className="justo-visitor__field">
                <span className="justo-visitor__label">
                  {strings.searchByMobileNumber}
                </span>
                <input
                  className="form-control justo-visitor__input"
                  type="text"
                  autoComplete="off"
                  placeholder={strings.searchMobileNumber}
                  name="search_by_mobile_number"
                  value={form.search_by_mobile_number}
                  onChange={handleInputField}
                  maxLength="10"
                />
              </label>
              <label className="justo-visitor__field">
                <span className="justo-visitor__label">
                  {strings.latestLeadStatus}
                </span>
                <select
                  className="form-control justo-visitor__input"
                  name="latest_lead_status"
                  onChange={handleInputField}
                  value={form.latest_lead_status}
                >
                  <option value="">{strings.selectLatestLeadStatus}</option>
                  {LATEST_LEAD_STATUS_FILTER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="justo-visitor__field">
                <span className="justo-visitor__label">
                  {strings.visitorStatusFilter}
                </span>
                <select
                  className="form-control justo-visitor__input"
                  name="qualified"
                  onChange={handleInputField}
                  value={form.qualified}
                >
                  <option value="">{strings.selectVisitorStatus}</option>
                  <option value={true}>{strings.qualified}</option>
                  <option value={false}>{strings.unQualified}</option>
                </select>
              </label>
              <label className="justo-visitor__field">
                <span className="justo-visitor__label">
                  {strings.leadPriority}
                </span>
                <select
                  className="form-control justo-visitor__input"
                  name="lead_priority"
                  onChange={handleInputField}
                  value={form.lead_priority}
                >
                  <option value="">{strings.selectLeadPriority}</option>
                  <option value="hot">{strings.hot}</option>
                  <option value="warm">{strings.warm}</option>
                  <option value="cold">{strings.cold}</option>
                </select>
              </label>
            </div>
            {(form.search_by_visisor_name?.trim() ||
              form.search_by_mobile_number?.trim()) && (
              <p
                className="justo-visitor__search-hint"
                style={{
                  margin: "0 0 12px",
                  fontSize: "0.8rem",
                  fontWeight: 400,
                  color: "#64748b",
                }}
              >
                {strings.depositionVisitorSearchHint}
              </p>
            )}
            <div className="justo-visitor__filter-footer">
              <div className="justo-visitor__filter-actions">
                <button
                  className="justo-visitor__btn justo-visitor__btn--solid"
                  type="button"
                  onClick={filterDetail}
                >
                  {strings.search}
                </button>
                <button
                  className="justo-visitor__btn justo-visitor__btn--ghost"
                  type="button"
                  onClick={filterReset}
                >
                  {strings.reset}
                </button>
              </div>
            </div>
          </form>

          <div className="justo-visitor__toolbar">
            <div className="justo-visitor__toolbar-sort">
              <label htmlFor="visitor-list-sort-by">{strings.sortBy}</label>
              <select
                id="visitor-list-sort-by"
                className="form-control justo-visitor__input"
                value={sortBy}
                onChange={handleSortChange}
              >
                <option value={VISITOR_SORT_OPTIONS.NEWEST}>
                  {strings.recentlyAddedNewest}
                </option>
                <option value={VISITOR_SORT_OPTIONS.OLDEST}>
                  {strings.sortOldestFirst}
                </option>
                <option value={VISITOR_SORT_OPTIONS.SCORE_HIGH}>
                  {strings.visitingScoreHighToLow}
                </option>
                <option value={VISITOR_SORT_OPTIONS.SCORE_LOW}>
                  {strings.visitingScoreLowToHigh}
                </option>
              </select>
            </div>
          </div>

          <div className="justo-visitor__table-wrap">
            <div className="table-responsive visitor-listing--hybrid__scroll">
              {loading ? (
                <TableListSkeleton rows={10} columns={9} />
              ) : (
                <>
                  <table className="table visitor-listing--hybrid__table">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="visitor-listing__text-left"
                        >
                          {strings.propertyName}
                        </th>
                        <th
                          scope="col"
                          className="visitor-listing__text-left"
                        >
                          {strings.visitorName}
                        </th>
                        <th
                          scope="col"
                          className="visitor-listing__text-center"
                        >
                          {strings.createdDate}
                        </th>
                        <th
                          scope="col"
                          className="visitor-listing__text-left"
                        >
                          {strings.sourceName}
                        </th>
                        <th
                          scope="col"
                          className="visitor-listing__text-center"
                        >
                          {strings.configuration}
                        </th>
                        <th
                          scope="col"
                          className="visitor-listing__text-center"
                        >
                          {strings.budget}
                        </th>
                        <th
                          scope="col"
                          className="visitor-listing__text-center"
                        >
                          {strings.visitScore}
                        </th>
                        <th
                          scope="col"
                          className="visitor-listing__text-center visitor-listing__status-col"
                        >
                          {strings.status}
                        </th>
                        {canAllocateToCM ? (
                          <th
                            scope="col"
                            className="visitor-listing__text-center visitor-listing__allocate-col"
                          >
                            {strings.allocatedToCM}
                          </th>
                        ) : null}
                        <th
                          scope="col"
                          className="visitor-listing__text-center visitor-listing__actions-col"
                        >
                          {strings.action}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {getvisitorList?.length > 0 ? (
                        (Array.isArray(getvisitorList)
                          ? getvisitorList
                          : []
                        ).map((item, index) => {
                          const showEditAction =
                            edit &&
                            item?.lead_status <= 3 &&
                            !misRole;
                          const showAllocateAction =
                            canAllocateToCM &&
                            item?.property_id &&
                            item?.lead_status <= 3;
                          const visitorAllocatedToCm =
                            isVisitorAllocatedToCm(item);
                          const showReallocateAction =
                            showAllocateAction && visitorAllocatedToCm;
                          const showFirstAllocateAction =
                            showAllocateAction && !visitorAllocatedToCm;
                          const currentCmId = getVisitorCurrentCmId(item);
                          const currentCmName = getVisitorCurrentCmName(item);
                          const visitorName =
                            `${item?.first_name || ""} ${item?.last_name || ""}`.trim();
                          const budgetText = formatVisitorBudget(
                            item,
                            !!cpId,
                          );
                          const statusDisplay = resolveVisitorDisplayStatus(item);
                          const statusLabel = statusDisplay.label || "";
                          const statusBadgeClass =
                            getVisitorDisplayBadgeClass(statusDisplay);
                          return (
                            <tr key={item._id || index}>
                              <td className="visitor-listing__text-left">
                                {renderVisitorCell(
                                  item?.property_title,
                                  "visitor-listing__primary",
                                )}
                              </td>
                              <td className="visitor-listing__text-left">
                                {renderVisitorCell(
                                  visitorName,
                                  "visitor-listing__primary",
                                )}
                              </td>
                              <td className="visitor-listing__text-center">
                                {renderVisitorCell(
                                  item.createdDate
                                    ? moment(item.createdDate).format(
                                        "DD-MM-yyyy hh:mm A",
                                      )
                                    : "",
                                  "visitor-listing__date",
                                )}
                              </td>
                              <td className="visitor-listing__text-left">
                                {renderVisitorCell(
                                  getVisitorSourceName(item),
                                  "visitor-listing__muted",
                                )}
                              </td>
                              <td className="visitor-listing__text-center">
                                {renderVisitorCell(
                                  item?.configuration,
                                  "visitor-listing__body",
                                )}
                              </td>
                              <td className="visitor-listing__text-center">
                                {renderVisitorCell(
                                  budgetText,
                                  "visitor-listing__muted",
                                )}
                              </td>
                              <td className="visitor-listing__text-center">
                                {renderVisitorCell(
                                  item?.lead_score,
                                  "visitor-listing__metric",
                                )}
                              </td>
                              <td className="visitor-listing__text-center visitor-listing__status-col">
                                <span
                                  className={`visitor-status-badge ${statusBadgeClass}`}
                                  title={statusLabel || "-"}
                                >
                                  <VisitorStatusIcon
                                    badgeClass={statusBadgeClass}
                                  />
                                  <span className="visitor-status-badge__label">
                                    {formatAppointmentCellValue(statusLabel)}
                                  </span>
                                </span>
                              </td>
                              {canAllocateToCM ? (
                                <td className="visitor-listing__text-center visitor-listing__allocate-col">
                                  <VisitorCmAllocationCell
                                    strings={strings}
                                    disabled={showAllocateModal}
                                    cmName={
                                      currentCmName || item?.created_for_cm_name
                                    }
                                    state={
                                      showReallocateAction
                                        ? "allocated"
                                        : showFirstAllocateAction
                                          ? "pending"
                                          : "inactive"
                                    }
                                    onAllocate={() => {
                                      setSelectedVisitor({
                                        leadId: item._id,
                                        propertyId: item.property_id,
                                        visitorName,
                                        isReallocate: false,
                                      });
                                      setShowAllocateModal(true);
                                    }}
                                    onReallocate={() => {
                                      setSelectedVisitor({
                                        leadId: item._id,
                                        propertyId: item.property_id,
                                        visitorName,
                                        currentCmId,
                                        currentCmName:
                                          currentCmName ||
                                          item.created_for_cm_name ||
                                          "",
                                        isReallocate: visitorAllocatedToCm,
                                      });
                                      setShowAllocateModal(true);
                                    }}
                                  />
                                </td>
                              ) : null}
                              <td className="visitor-listing__text-center visitor-listing__actions-col">
                                <div className="visitor-listing__action-group">
                                  <span className="visitor-listing__action-slot">
                                    {view ? (
                                      <button
                                        type="button"
                                        className="visitor-listing__action-btn"
                                        title={strings.viewVisitor}
                                        onClick={() => {
                                          navigatpage(
                                            `/viewvisitor/${item._id}`,
                                          );
                                        }}
                                      >
                                        <i
                                          className="fa fa-eye"
                                          aria-hidden="true"
                                        />
                                      </button>
                                    ) : (
                                      <span
                                        className="visitor-listing__action-btn visitor-listing__action-btn--placeholder"
                                        aria-hidden="true"
                                      />
                                    )}
                                  </span>
                                  <span className="visitor-listing__action-slot">
                                    {showEditAction ? (
                                      <button
                                        type="button"
                                        className="visitor-listing__action-btn"
                                        title={strings.editVisitor}
                                        onClick={() => {
                                          const path =
                                            item.draft === true
                                              ? `/visitor_form_draft?lead_id=${item._id}`
                                              : `/visitor_form?lead_id=${item._id}`;
                                          navigatpage(path);
                                        }}
                                      >
                                        <i
                                          className="fa fa-edit"
                                          aria-hidden="true"
                                        />
                                      </button>
                                    ) : (
                                      <span
                                        className="visitor-listing__action-btn visitor-listing__action-btn--placeholder"
                                        aria-hidden="true"
                                      />
                                    )}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr />
                      )}
                    </tbody>
                  </table>
                  <TableDataNotFound data={getvisitorList} />
                </>
              )}
            </div>
            <Pagination
              totalCount={visitorListSelector?.response?.total_data}
              listData={getvisitorList}
              onPageChange={handlePageClick}
              itemOffset={page}
              setItemOffset={setPage}
            />
          </div>
        </div>
      </div>

      {canAllocateToCM && selectedVisitor && (
        <AllocateSM
          show={showAllocateModal}
          propertyId={selectedVisitor.propertyId}
          leadId={selectedVisitor.leadId}
          visitorName={selectedVisitor.visitorName}
          isReallocate={selectedVisitor.isReallocate}
          currentCmId={selectedVisitor.currentCmId}
          currentCmName={selectedVisitor.currentCmName}
          handleClose={() => {
            setShowAllocateModal(false);
            setSelectedVisitor(null);
          }}
          onAllocateSuccess={() => {
            const leadIdToRemove = selectedVisitor?.leadId;
            const isReallocate = selectedVisitor?.isReallocate;
            setShowAllocateModal(false);
            setSelectedVisitor(null);
            if (leadIdToRemove && !isReallocate) {
              lastAllocatedLeadIdRef.current = leadIdToRemove;
              const idStr = String(leadIdToRemove);
              setVisitorList((prev) =>
                Array.isArray(prev)
                  ? prev.filter(
                      (v) => String(v._id || v.lead_id || "") !== idStr,
                    )
                  : prev,
              );
            }
            fetchVisitorList(page);
          }}
        />
      )}
    </div>
  );
};
export default Visitor;
